/**
 * Student Embeddings Cache - IndexedDB Storage
 * Stores student embeddings in browser for fast offline recognition
 * Cache persists FOREVER until manually cleared
 */

const DB_NAME = 'SmartAttendanceDB';
const DB_VERSION = 2;
const STUDENTS_STORE = 'students';
const ATTENDANCE_STORE = 'pendingAttendance';
const CACHE_KEY = 'students_cache_metadata';

/**
 * Initialize IndexedDB with multiple stores
 */
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object store for students
      if (!db.objectStoreNames.contains(STUDENTS_STORE)) {
        const store = db.createObjectStore(STUDENTS_STORE, { keyPath: 'rollNo' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('_id', '_id', { unique: true });
      }
      
      // Create object store for pending attendance records
      if (!db.objectStoreNames.contains(ATTENDANCE_STORE)) {
        const attendanceStore = db.createObjectStore(ATTENDANCE_STORE, { keyPath: 'id', autoIncrement: true });
        attendanceStore.createIndex('synced', 'synced', { unique: false });
        attendanceStore.createIndex('timestamp', 'timestamp', { unique: false });
        attendanceStore.createIndex('schoolCode', 'schoolCode', { unique: false });
      }
    };
  });
}

/**
 * Store students in IndexedDB
 * Cache persists FOREVER - no expiration
 */
export async function cacheStudents(students) {
  try {
    const db = await openDB();
    const transaction = db.transaction([STUDENTS_STORE], 'readwrite');
    const store = transaction.objectStore(STUDENTS_STORE);
    
    // Clear existing data
    await store.clear();
    
    // Store each student
    let validCount = 0;
    for (const student of students) {
      // Only cache students with valid embeddings
      const embedding = student.embedding || student.faceEmbedding;
      if (embedding && Array.isArray(embedding) && embedding.length > 0) {
        await store.put({
          _id: student._id,
          rollNo: student.rollNo,
          name: student.name,
          class: student.class,
          section: student.section,
          embedding: embedding,
          cachedAt: Date.now()
        });
        validCount++;
      }
    }
    
    // Store metadata - NO EXPIRATION
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      count: validCount,
      lastUpdated: Date.now(),
      version: DB_VERSION,
      expiresAt: null // NULL = Cache forever
    }));
    
    return { success: true, count: validCount };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Get all cached students
 */
export async function getCachedStudents() {
  try {
    const db = await openDB();
    const transaction = db.transaction([STUDENTS_STORE], 'readonly');
    const store = transaction.objectStore(STUDENTS_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const students = request.result;
        resolve(students);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    return [];
  }
}

/**
 * Get cache metadata
 */
export function getCacheMetadata() {
  try {
    const metadata = localStorage.getItem(CACHE_KEY);
    return metadata ? JSON.parse(metadata) : null;
  } catch (err) {
    return null;
  }
}

/**
 * Check if cache exists (no staleness check - cache is forever)
 */
export function isCacheAvailable() {
  const metadata = getCacheMetadata();
  return metadata !== null && metadata.count > 0;
}

/**
 * Clear cache manually
 */
export async function clearCache() {
  try {
    const db = await openDB();
    const transaction = db.transaction([STUDENTS_STORE], 'readwrite');
    const store = transaction.objectStore(STUDENTS_STORE);
    await store.clear();
    localStorage.removeItem(CACHE_KEY);
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Cosine similarity between two vectors (embeddings)
 */
export function cosineSimilarity(vecA, vecB) {
  if (!vecA || !vecB || vecA.length !== vecB.length) return 0;
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < vecA.length; i++) {
    dotProduct += vecA[i] * vecB[i];
    normA += vecA[i] * vecA[i];
    normB += vecB[i] * vecB[i];
  }
  
  normA = Math.sqrt(normA);
  normB = Math.sqrt(normB);
  
  if (normA === 0 || normB === 0) return 0;
  
  return dotProduct / (normA * normB);
}

/**
 * Match embedding against cached students (FAST in-browser recognition)
 * Returns best match if similarity > threshold
 */
export async function matchEmbeddingInCache(embedding, threshold = 0.5) {
  try {
    const startTime = performance.now();
    const cachedStudents = await getCachedStudents();
    
    if (cachedStudents.length === 0) {
      return null;
    }
    
    let bestMatch = null;
    let bestSimilarity = 0;
    
    // Compare against all cached embeddings
    for (const student of cachedStudents) {
      const similarity = cosineSimilarity(embedding, student.embedding);
      
      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestMatch = student;
      }
    }
    
    const duration = performance.now() - startTime;
    
    if (bestMatch && bestSimilarity >= threshold) {
      return {
        student: bestMatch,
        similarity: bestSimilarity,
        duration: duration
      };
    }
    return null;
  } catch (err) {
    return null;
  }
}

/**
 * Batch match multiple embeddings against cache
 * Returns array of matches
 */
export async function batchMatchInCache(embeddingsData, threshold = 0.5) {
  try {
    const startTime = performance.now();
    const cachedStudents = await getCachedStudents();
    
    if (cachedStudents.length === 0) {
      return { matches: [], duration: 0 };
    }
    
    const matches = [];
    
    for (const embData of embeddingsData) {
      let bestMatch = null;
      let bestSimilarity = 0;
      
      for (const student of cachedStudents) {
        const similarity = cosineSimilarity(embData.embedding, student.embedding);
        
        if (similarity > bestSimilarity) {
          bestSimilarity = similarity;
          bestMatch = student;
        }
      }
      
      if (bestMatch && bestSimilarity >= threshold) {
        matches.push({
          trackId: embData.trackId,
          student: {
            _id: bestMatch._id,
            name: bestMatch.name,
            rollNo: bestMatch.rollNo,
            class: bestMatch.class,
            section: bestMatch.section
          },
          similarity: bestSimilarity
        });
      }
    }
    
    const duration = performance.now() - startTime;
    
    return { matches, duration, count: matches.length };
  } catch (err) {
    return { matches: [], duration: 0, count: 0 };
  }
}
